import {
  Link,
  createFileRoute,
  redirect,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIcon,
  BoxesIcon,
  CheckIcon,
  ChevronUpIcon,
  EyeOffIcon,
  FileIcon,
  SunIcon,
} from "lucide-react";
import { useMap, useSet } from "@uidotdev/usehooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCommandScore } from "use-command-score";
import ky from "ky";
import type { Policy } from "@/lib/queries/governance/policies";
import type { DateString } from "@/lib/utils/date";
import { invariant } from "@/lib/invariant";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { governancePoliciesQueryOptions } from "@/lib/queries/governance/policies";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { governanceEntitiesQueryOptions } from "@/lib/queries/governance/entities";
import { LoadingSpinner } from "@/components/loading";
import { api } from "@/lib/http";
import { FilePreview } from "@/components/filePreview";
import { SubmitterText } from "@/components/ui/submit-button";

export const Route = createFileRoute("/_app/document-redactor/options")({
  component: RouteComponent,
  beforeLoad({ location: { state } }) {
    if (!state.documentRedactFile) throw redirect({ to: "/document-redactor" });
  },
  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(governancePoliciesQueryOptions);
    queryClient.ensureQueryData(governanceEntitiesQueryOptions);
  },
});

type RedactionType = "highlight" | "redact";
type RedactionScope = "regular" | "contextual";

type DocumentRedactResult = {
  output_file_url: string;
  message: string;
};

type TaskMetaBase = {
  id: string;
  created_at: DateString;
  file_upload_url?: string;
};

type TaskWaiting = {
  status: "waiting_for_file";
  started_at: null;
  finished_at: null;
  result: null;
};

type TaskRunning = {
  status: "running";
  started_at: DateString;
  finished_at: null;
  result: null;
};

type TaskDone = {
  status: "done";
  started_at: DateString;
  finished_at: DateString;
  result: DocumentRedactResult;
};

type TaskFailed = {
  status: "failed";
  started_at: DateString;
  finished_at: DateString;
  result: null;
};

type TaskMeta = TaskMetaBase &
  (TaskWaiting | TaskRunning | TaskDone | TaskFailed);

function RouteComponent() {
  const {
    state: { documentRedactFile: file },
  } = useLocation();

  const entities = useQuery({
    ...governanceEntitiesQueryOptions,
    select(data) {
      return new Set(data.entity_list);
    },
  });

  const selectedEntities = useSet<string>();
  const selectedPolicies = useMap<string, Policy>();
  useEffect(() => {
    if (entities.isSuccess) {
      selectedEntities.clear();
      entities.data.values().forEach(selectedEntities.add);
    }
  }, [entities.isSuccess]);

  const allEntitiesSelected =
    entities.data?.size === selectedEntities.size &&
    entities.data.isSubsetOf(selectedEntities);

  const policyEntities = new Set(
    selectedPolicies.values().flatMap((p) => p.entities_aggregated),
  );

  const combinedEntities = selectedEntities.union(policyEntities);

  const togglePii = useCallback(
    (pii: string) => {
      if (policyEntities.has(pii)) return;
      selectedEntities.has(pii)
        ? selectedEntities.delete(pii)
        : selectedEntities.add(pii);
    },
    [selectedEntities],
  );

  const [redactionType, setRedactionType] = useState<RedactionType>("redact");

  const [redactionScope, setRedactionScope] =
    useState<RedactionScope>("regular");

  const [searchEntities, setSearchEntities] = useState("");
  const [showMore, setShowMore] = useState(false);
  const foundEntities = useCommandScore(searchEntities, [
    ...(entities.data?.values() ?? []),
  ]);
  const hasMoreEntities = foundEntities.length > 10;

  const entitiesToShow = showMore ? foundEntities : foundEntities.slice(0, 10);

  const navigate = Route.useNavigate();
  const router = useRouter();
  const redactFile = useMutation({
    async mutationFn(data: {
      scope: RedactionScope;
      type: RedactionType;
      entities: Array<string>;
      policies: Array<string>;
      file: File;
    }) {
      const task_submit = await api.governance
        .post<{
          id: string;
          upload_url: string;
        }>("gcp/pii/pdf-editor/getting-upload-url", {
          json: {
            detect_pid: data.scope === "contextual",
            action: data.type,
            detect_signature: data.scope === "contextual",
            detect_fingerprint: data.scope === "contextual",
            entity_list: data.entities,
            policy_list: data.policies,
          },
        })
        .json();

      await ky.put(task_submit.upload_url, { body: file });
      await api.governance.post(
        `gcp/pii/pdf-editor/${task_submit.id}/complete_upload`,
      );

      let meta: TaskMeta;
      do {
        meta = await api.governance
          .get<TaskMeta>(`gcp/pii/pdf-editor/${task_submit.id}`)
          .json();
        invariant(
          meta.status !== "waiting_for_file",
          "file should have already been uploaded",
        );
        if (meta.status === "failed") throw new Error("Task Failed");

        await new Promise((r) => setTimeout(r, 1000));
      } while (meta.status !== "done");
      return meta.result;
    },
    onMutate() {
      router.preloadRoute({ from: Route.fullPath, to: "../result" });
    },
    onSuccess(data) {
      navigate({
        from: Route.fullPath,
        to: "../result",
        state: { documentRedactResult: data },
        mask: {
          from: Route.fullPath,
          to: "..",
        },
      });
    },
  });

  if (!file) return null;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 lg:flex-row">
      <div className="flex-1">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Document Redactor</h1>
            <p className="text-muted-foreground">
              Highlights PIIs on your files
            </p>
          </div>
          <Link
            className={cn(buttonVariants({ variant: "outline" }), "gap-2")}
            to=".."
            replace
          >
            <FileIcon className="h-4 w-4" />
            Start new
          </Link>
        </div>

        <Card className="relative flex h-90 w-full items-center justify-center overflow-clip border-dashed bg-foreground/10 p-2 has-data-[previewing]:h-180">
          <FilePreview file={file} />
        </Card>
      </div>

      <div className="w-full lg:w-[400px]">
        <h2 className="mb-6 text-xl font-semibold">
          Configure redaction settings
        </h2>

        <div className="space-y-6">
          <div>
            <label className="mb-2 block font-medium">
              Redaction type<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <Button
                className="flex-1 justify-start gap-2"
                variant={redactionType === "highlight" ? "default" : "outline"}
                onClick={() => setRedactionType("highlight")}
              >
                <SunIcon className="h-4 w-4" />
                Highlight PIIs
              </Button>
              <Button
                className="flex-1 justify-start gap-2"
                variant={redactionType === "redact" ? "default" : "outline"}
                onClick={() => setRedactionType("redact")}
              >
                <EyeOffIcon className="h-4 w-4" />
                Mask PIIs
              </Button>
            </div>
          </div>
          <div>
            <label className="mb-2 block font-medium">
              Redaction scope<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <Button
                className="flex-1 justify-start gap-2"
                variant={redactionScope === "regular" ? "default" : "outline"}
                onClick={() => setRedactionScope("regular")}
              >
                <ActivityIcon className="h-4 w-4" />
                Regular
              </Button>
              <Button
                className="flex-1 justify-start gap-2"
                variant={
                  redactionScope === "contextual" ? "default" : "outline"
                }
                onClick={() => setRedactionScope("contextual")}
              >
                <BoxesIcon className="h-4 w-4" />
                Contextual
              </Button>
            </div>
          </div>

          <div>
            <label className="mb-2 block font-medium">
              Select PIIs<span className="text-red-500">*</span>
            </label>

            <PolicyCombobox
              selectedPolicies={selectedPolicies}
              onPolicySelect={(k, p) =>
                selectedPolicies.has(k)
                  ? selectedPolicies.delete(k)
                  : selectedPolicies.set(k, p)
              }
            />

            <Input
              value={searchEntities}
              onChange={(e) => {
                setSearchEntities(e.target.value);
                setShowMore(false);
              }}
              type="search"
              placeholder="Search"
              className="my-2"
            />

            {entities.isSuccess ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() =>
                    allEntitiesSelected
                      ? selectedEntities.clear()
                      : entities.data.values().forEach(selectedEntities.add)
                  }
                >
                  {!allEntitiesSelected ? "Select All" : "Deselect All"}
                </Button>

                <div className="mt-4 flex flex-wrap gap-2">
                  {entitiesToShow.map((pii) => (
                    <Button
                      key={pii}
                      variant="outline"
                      size="sm"
                      onClick={() => togglePii(pii)}
                      data-selected={
                        selectedEntities.has(pii) ? "true" : undefined
                      }
                      data-forced={policyEntities.has(pii) ? "true" : undefined}
                      className={cn(
                        "rounded-full text-sm",
                        "data-forced:bg-primary data-forced:text-white",
                        "dark:data-forced:bg-gray-700",
                        "data-selected:bg-gray-900 data-selected:text-white",
                        "dark:data-selected:bg-gray-100 dark:data-selected:text-black",
                      )}
                    >
                      {pii}
                    </Button>
                  ))}
                </div>

                {hasMoreEntities && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowMore(!showMore)}
                    className="mt-1 px-1.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    {showMore ? "Show Less" : "Show More"}
                  </Button>
                )}
              </>
            ) : entities.isLoading ? (
              <LoadingSpinner className="my-5" />
            ) : (
              "Error!"
            )}
          </div>
        </div>

        <div className="mt-8">
          <Button
            className="w-full"
            size="lg"
            onClick={() =>
              redactFile.mutate({
                file,
                scope: redactionScope,
                type: redactionType,
                entities: [...selectedEntities],
                policies: [...selectedPolicies.keys()],
              })
            }
            disabled={combinedEntities.size < 1}
          >
            <SubmitterText
              submitting={redactFile.isPending}
              submittingChildren={"Redacting..."}
            >
              {combinedEntities.size < 1
                ? "Please select at least one policy or entity"
                : "Redact"}
            </SubmitterText>
          </Button>
          {redactFile.isError && (
            <span className="text-destructive-foreground">
              {redactFile.error.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function PolicyCombobox({
  selectedPolicies,
  onPolicySelect,
}: {
  selectedPolicies: Map<string, Policy>;
  onPolicySelect: (k: string, p: Policy) => void;
}) {
  const selectedKeys = [...selectedPolicies.keys()];
  const policies = useQuery({
    ...governancePoliciesQueryOptions,
    select: (data) => Object.entries(data),
  });

  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="group/popover-trigger flex w-full justify-between"
        >
          {selectedKeys.length > 0
            ? selectedKeys.map((k) => selectedPolicies.get(k)!.name).join(",")
            : "Select Policies"}
          <ChevronUpIcon className="ml-auto size-4 shrink-0 justify-self-end opacity-50 transition-transform group-aria-expanded/popover-trigger:-rotate-180" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-100 p-0">
        <Command className="p-2">
          <CommandInput placeholder="Search policy..." />
          {policies.isLoading ? (
            <LoadingSpinner />
          ) : policies.isSuccess ? (
            <CommandList>
              <CommandEmpty>No Policy Found.</CommandEmpty>
              <CommandGroup>
                {policies.data.map(([k, policy]) => (
                  <CommandItem
                    className="group/command-item flex w-full"
                    key={k}
                    value={k}
                    data-picked={selectedPolicies.has(k) ? "true" : undefined}
                    keywords={[policy.name, policy.description]}
                    onSelect={() => onPolicySelect(k, policy)}
                  >
                    {policy.name}
                    <CheckIcon className="ml-auto justify-self-end group-not-data-picked/command-item:hidden" />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          ) : (
            "Error!"
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
